import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Check, LayoutDashboard, Users, Zap, UserPlus, Sparkles, BookOpen } from "lucide-react";

const PAGES = [
  {
    icon: BookOpen,
    title: "Welcome to Split the ____",
    body: (
      <>
        <p>Split bills with friends, groups, or 1-on-1 — without the math.</p>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>• Track who owes what across rent, trips, dinners.</li>
          <li>• Smart settlements minimise payments across the group.</li>
          <li>• Direct Splits work even without a group.</li>
        </ul>
      </>
    ),
  },
  {
    icon: LayoutDashboard,
    title: "Your dashboard",
    body: (
      <>
        <p>Your Dashboard shows your net balance, AI insights, spending by month and category, top creditors, active groups and recent bills.</p>
        <p className="text-sm text-muted-foreground">Tap any widget to dive deeper.</p>
      </>
    ),
  },
  {
    icon: Users,
    title: "Create a group & add bills",
    body: (
      <>
        <p>Tap <strong>New group</strong> from Groups → name it → add friends instantly from your friends list or share an invite link/code.</p>
        <p>Inside the group, hit <strong>Add bill</strong> and pick a split: Equal, Exact, Percent, or Shares.</p>
      </>
    ),
  },
  {
    icon: Zap,
    title: "Smart vs Simple settlements",
    body: (
      <>
        <p><strong>Simple</strong> shows each individual debt separately.</p>
        <p><strong>Smart</strong> minimises the total number of payments by netting balances across the whole group.</p>
        <p className="text-sm text-muted-foreground">Tap the toggle in every group&apos;s settlements panel.</p>
      </>
    ),
  },
  {
    icon: UserPlus,
    title: "Direct splits without a group",
    body: (
      <>
        <p>For 1-on-1 IOUs, open <strong>Friends</strong> → tap any friend → use <strong>Split</strong> or <strong>Settle up</strong>. No group required.</p>
        <p className="text-sm text-muted-foreground">Use the History tab to see every transaction with that person.</p>
      </>
    ),
  },
  {
    icon: Sparkles,
    title: "PRO features",
    body: (
      <>
        <p>The <strong>PRO Dashboard</strong> tracks your income & expenses, savings goals, recurring bills and a unified payments ledger.</p>
        <p>Save split templates (&ldquo;Flat Rent&rdquo;, &ldquo;Office Lunch&rdquo;) and reuse them in one tap.</p>
        <p className="text-xs font-mono uppercase tracking-wider brand-text">Free during beta</p>
      </>
    ),
  },
];

export const UserGuide = ({ open, onOpenChange }) => {
  const [i, setI] = useState(0);
  const page = PAGES[i];
  const Icon = page.icon;
  const isLast = i === PAGES.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setI(0); }}>
      <DialogContent className="bg-card border-border max-w-md max-h-[90vh] overflow-y-auto" data-testid="user-guide-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl brand-bg text-black flex items-center justify-center">
              <Icon className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground" data-testid="guide-step">
                Step {i + 1} of {PAGES.length}
              </div>
              <DialogTitle className="font-heading text-2xl">{page.title}</DialogTitle>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-3 text-sm leading-relaxed" data-testid="guide-body">
          {page.body}
        </div>
        <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t border-border">
          <Button
            variant="outline"
            onClick={() => setI((v) => Math.max(0, v - 1))}
            disabled={i === 0}
            className="border-border h-11"
            data-testid="guide-prev"
          >
            <ArrowLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Back
          </Button>
          <div className="flex gap-1.5">
            {PAGES.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 brand-bg" : "w-1.5 bg-foreground/15"}`}
              />
            ))}
          </div>
          <Button
            onClick={() => { if (isLast) onOpenChange(false); else setI((v) => v + 1); }}
            className="brand-bg text-black hover:opacity-90 h-11"
            data-testid={isLast ? "guide-done" : "guide-next"}
          >
            {isLast ? (<><Check className="h-4 w-4 mr-1" strokeWidth={2} /> Done</>) : (<>Next <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} /></>)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
